import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription, combineLatest, Subject } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { PoolDid } from "../../../../../../common/services/api/resources/did/pool-did";
import { DidService } from "../../../../../../common/services/api/resources/did/did.service";
import { PoolDidService } from "../../../../../../common/services/api/resources/did/pool-did.service";
import { FlowrouteNumberService } from "../../../../../../common/services/api/resources/flowrouteNumbers/flowroute-number.service";
import {
  NpaNxx,
  Nxx,
  TOLL_FREE_NPAS
} from "../../../../../../common/services/api/resources/npanxx/npanxx";
import { FlowrouteNumber } from "../../../../../../common/services/api/resources/flowrouteNumbers/flowroute-numbers";
import { CheckListItem } from "../../../../shared/components/onsipItemList/checkList/check-item-list.component";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { SnackbarService } from "../../../../shared/components/snackbar/snackbar.service";

enum Step {
  FindNumbers,
  SearchingNumbers,
  SelectNumbers,
  PurchaseNumbers
}

const SPINNER_DIAMETER = 120;
const SETUP_COST = 7;
const MONTHLY_COST = 2;

@Component({
  selector: "onsip-add-multiple-phone-number",
  templateUrl: "./add-multiple-phone-number.component.html",
  styleUrls: ["./add-multiple-phone-number.component.scss"],
  standalone: false
})
export class AddMultiplePhoneNumberComponent implements OnInit, OnDestroy {
  @Input() npaNxxs!: Array<NpaNxx>;
  @Output() closeDialog = new EventEmitter<void>();
  @Output() showSearchScreen = new EventEmitter<boolean>(false);
  @Output() showPurchaseSpinner = new EventEmitter<boolean>(false);
  nxxs: Array<Nxx> = [];
  /** we will use a behaviorsubject to track what step the user is on */
  currentStep = new BehaviorSubject<Step>(Step.FindNumbers);
  listOfPoolDids: Array<PoolDid> = [];
  listOfFlowrouteNumbers: Array<FlowrouteNumber> = [];
  listOfAvailableNumbers: Array<CheckListItem> = [];
  stepType = Step;
  spinnerDiameter = SPINNER_DIAMETER;
  setupCost = SETUP_COST;
  monthlyCost = MONTHLY_COST;
  isMobile = this.breakpointObserver
    .observe(Breakpoints.XSmall)
    .pipe(map(result => result.matches));

  quantityArray = Array(20)
    .fill(0)
    .map((_, idx) => idx + 1);

  multiplePhoneNumber = this.fb.group({
    quantity: this.fb.control(0, [Validators.required]),
    npa: this.fb.control("", Validators.required),
    nxx: this.fb.control("")
  });
  // flag to let modal know when the actual api purchasing step is happening
  purchasingFlag = false;

  /** listener that will trigger a didRequest api action */
  private didRequestSubject = new Subject<number>();
  private unsubscriber = new Subscription();

  constructor(
    private fb: NonNullableFormBuilder,
    private translate: TranslateService,
    private didService: DidService,
    private poolDidService: PoolDidService,
    private flowrouteNumberService: FlowrouteNumberService,
    private breakpointObserver: BreakpointObserver,
    private snackbar: SnackbarService
  ) {}

  ngOnInit() {
    this.unsubscriber.add(
      this.multiplePhoneNumber.controls.npa.valueChanges.subscribe(
        this.onAreaCodeChanges.bind(this)
      )
    );

    this.unsubscriber.add(
      this.currentStep.subscribe(step => {
        this.showSearchScreen.emit(step === Step.SearchingNumbers);
      })
    );

    this.unsubscriber.add(
      this.didRequestSubject.subscribe(requests => this.callDidRequest(requests))
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  closeAddNumberForm(): void {
    this.closeDialog.emit();
  }

  getNpaDescription(npaNxx: NpaNxx): string {
    if (TOLL_FREE_NPAS.includes(npaNxx.num)) return this.translate.instant("ONSIP_I18N.TOLL_FREE");
    return npaNxx?.postalCode?.name || this.translate.instant("ONSIP_I18N.UNKNOWN");
  }

  navigateToSearch(): void {
    if (this.multiplePhoneNumber.invalid) {
      this.multiplePhoneNumber.markAllAsTouched();
      return;
    }
    const quantity = this.multiplePhoneNumber.controls.quantity.value;
    this.currentStep.next(Step.SearchingNumbers);

    // find available numbers from our poolDid and Flowroute Number frontend store first
    // before going to the backend
    this.unsubscriber.add(
      combineLatest([
        this.poolDidService.state.pipe(
          map(state => Object.values(state.state)),
          take(1)
        ),
        this.flowrouteNumberService.state.pipe(
          map(state => Object.values(state.state)),
          take(1)
        )
      ])
        .pipe(take(1))
        .subscribe(([poolDids, frNumbers]) => {
          this.filterAndSortNumbers(poolDids, frNumbers);
          if (this.listOfAvailableNumbers.length < quantity) {
            // emit event to trigger didRequest since we do not have enough requested numbers
            // fetch the remaining numbers
            const remainingNum = quantity - this.listOfAvailableNumbers.length;
            this.didRequestSubject.next(remainingNum);
          } else {
            // we found enough numbers in the store so do not need to fetch from api
            this.currentStep.next(Step.SelectNumbers);
          }
        })
    );
  }

  navigateToPurchase(): void {
    this.currentStep.next(Step.PurchaseNumbers);
  }

  submit(): void {
    if (this.multiplePhoneNumber.invalid) return;

    if (!this.listOfAvailableNumbers.length) {
      this.closeAddNumberForm();
      return;
    }

    const selectedPoolDids = this.listOfPoolDids.map(poolDid => poolDid.poolDidId);
    const selectedFrNumbers = this.listOfFlowrouteNumbers.map(frNumber => frNumber.value);

    // steps to show spinner
    this.purchasingFlag = true;
    this.showPurchaseSpinner.emit(true);

    this.didService
      .didRequestFillPoolDid({
        PoolDidIds: selectedPoolDids,
        FlowrouteNumbers: selectedFrNumbers
      })
      .then(result => {
        if (result.status === "success") {
          const filledNumbers = Object.values(result.data);
          // show snackbar if at least one number is filled but not all the selected numbers
          if (
            filledNumbers.length > 0 &&
            filledNumbers.length !== this.listOfAvailableNumbers.length
          ) {
            this.snackbar.openSnackBar(
              `We are sorry, but ${
                this.listOfAvailableNumbers.length - filledNumbers.length
              } of the selected numbers are no longer available. A representative will contact you ASAP to complete your order.
              `,
              "error"
            );
            this.poolDidService.removeFilledPoolDids(selectedPoolDids);
            this.flowrouteNumberService.removeFilledNumber(selectedFrNumbers);
            this.closeAddNumberForm();
          } else if (filledNumbers.length === this.listOfAvailableNumbers.length) {
            this.snackbar.openSnackBar(
              "Your new phone numbers have been added. Click on a number to assign it to a specific destination.",
              "success"
            );
            this.poolDidService.removeFilledPoolDids(selectedPoolDids);
            this.flowrouteNumberService.removeFilledNumber(selectedFrNumbers);
            this.didService.didBrowse();
            this.closeAddNumberForm();
          } else {
            this.snackbar.openSnackBar(
              "We are sorry, but we couldn't fill the selected numbers. Please try again or contact support.",
              "error"
            );
            this.purchasingFlag = false;
            this.showPurchaseSpinner.emit(false);
          }
        } else {
          const errorMessage = result.data.message;
          this.snackbar.openSnackBar(errorMessage, "error");
          this.didService.clearErrors();
          this.purchasingFlag = false;
          this.showPurchaseSpinner.emit(false);
        }
      });
  }

  private filterAndSortNumbers(poolDids: Array<PoolDid>, frNumbers: Array<FlowrouteNumber>): void {
    const npa = this.multiplePhoneNumber.controls.npa.value;
    const nxx = this.multiplePhoneNumber.controls.nxx.value;
    const quantity = this.multiplePhoneNumber.controls.quantity.value;

    let numberIndex = "1" + npa;
    if (nxx) numberIndex += nxx;
    this.listOfPoolDids = poolDids.filter(poolDid => poolDid.number.startsWith(numberIndex));
    this.listOfFlowrouteNumbers = frNumbers.filter(frNumber =>
      frNumber.value.startsWith(numberIndex)
    );
    this.listOfAvailableNumbers = this.listOfPoolDids
      .map(poolDid => poolDid.number)
      .concat(this.listOfFlowrouteNumbers.map(frNumber => frNumber.value))
      .map(number => ({
        name: number,
        checked: false,
        disabled: false
      }))
      .sort((a, b) => parseInt(a.name) - parseInt(b.name))
      .slice(0, quantity + 1);
  }

  private callDidRequest(requests: number = 0): void {
    const Npa = this.multiplePhoneNumber.controls.npa.value;
    const Nxx = this.multiplePhoneNumber.controls.nxx.value;
    // if requests are empty or zero than use Quantity from form
    const Quantity = requests || this.multiplePhoneNumber.controls.quantity.value;
    this.didService
      .didRequestWithoutFill({
        Npa,
        Nxx,
        Quantity
      })
      .then(result => {
        if (result.status === "success") {
          this.unsubscriber.add(
            combineLatest([
              this.poolDidService.state.pipe(
                filter(({ loading }) => !loading),
                map(state => Object.values(state.state)),
                take(1)
              ),
              this.flowrouteNumberService.state.pipe(
                filter(({ loading }) => !loading),
                map(state => Object.values(state.state)),
                take(1)
              )
            ])
              .pipe(take(1))
              .subscribe(([poolDids, frNumbers]) => {
                this.filterAndSortNumbers(poolDids, frNumbers);
                this.currentStep.next(Step.SelectNumbers);
              })
          );
        } else {
          this.didService.clearErrors();
        }
      });
  }

  private onAreaCodeChanges(npaNum: string) {
    const foundNpaNxx = this.npaNxxs.find(npaNxx => npaNxx.num === npaNum);
    this.nxxs = foundNpaNxx
      ? foundNpaNxx.nxxs.sort((a, b) => (a.rateCenter + a.num < b.rateCenter + b.num ? -1 : 1))
      : [];
    this.multiplePhoneNumber.controls.nxx.setValue("");
  }
}
